import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, Calendar, CheckCircle2, XCircle, CalendarOff, Gift, Sun } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { attendanceAPI } from '@/lib/api';
import { formatTime, formatDateLong } from '@/lib/dateUtils';
import BottomNav from '@/components/BottomNav';

const STATUS_CONFIG = {
  present: { bg: 'bg-[#026536]', text: 'text-white', label: 'P' },
  absent: { bg: 'bg-red-500', text: 'text-white', label: 'A' },
  leave: { bg: 'bg-[#dea439]', text: 'text-white', label: 'L' },
  holiday: { bg: 'bg-purple-500', text: 'text-white', label: 'H' },
  weekend: { bg: 'bg-[#1a2529]/10', text: 'text-[#1a2529]/30', label: 'W' },
  future: { bg: 'bg-transparent', text: 'text-[#1a2529]/15', label: '-' },
};

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function EmployeeHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    fetchMonthly();
  }, [month]);

  const fetchMonthly = async () => {
    setLoading(true);
    setSelectedDay(null);
    try {
      const res = await attendanceAPI.monthly(month);
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  })();

  // Build calendar grid with leading empty cells for alignment
  const calendarGrid = (() => {
    if (!data?.days) return [];
    const firstDay = new Date(data.days[0]?.date);
    let dow = firstDay.getDay(); // 0=Sun
    dow = dow === 0 ? 6 : dow - 1; // Convert to Mon=0
    const grid = Array(dow).fill(null);
    return [...grid, ...data.days];
  })();

  const s = data?.summary || {};

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#f4f4f5] pb-28">
      <div className="bg-[#026536] pt-12 pb-6 px-5">
        <div className="max-w-lg mx-auto">
          <h1 data-testid="history-title" className="text-xl font-extrabold text-white tracking-tight">Attendance</h1>
          <p className="text-white/60 text-xs mt-1">Monthly calendar view</p>
        </div>
      </div>

      <div className="px-4 -mt-3 max-w-lg mx-auto space-y-3">
        {/* Month Navigation */}
        <div data-testid="month-nav" className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between animate-slide-up">
          <button data-testid="prev-month-btn" onClick={() => changeMonth(-1)} className="h-9 w-9 rounded-xl bg-[#f4f4f5] flex items-center justify-center hover:bg-[#026536]/10 transition-colors">
            <ChevronLeft size={18} className="text-[#1a2529]" />
          </button>
          <div className="text-center">
            <p className="font-bold text-[#1a2529] text-sm">{monthLabel}</p>
          </div>
          <button data-testid="next-month-btn" onClick={() => changeMonth(1)} className="h-9 w-9 rounded-xl bg-[#f4f4f5] flex items-center justify-center hover:bg-[#026536]/10 transition-colors">
            <ChevronRight size={18} className="text-[#1a2529]" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* Summary Bar */}
            <div data-testid="summary-bar" className="grid grid-cols-5 gap-2 animate-slide-up">
              <SummaryPill icon={<CheckCircle2 size={13} />} count={s.present} label="Present" color="text-[#026536]" bg="bg-[#026536]/10" />
              <SummaryPill icon={<XCircle size={13} />} count={s.absent} label="Absent" color="text-red-500" bg="bg-red-50" />
              <SummaryPill icon={<Clock size={13} />} count={s.late} label="Late" color="text-[#dea439]" bg="bg-[#dea439]/10" />
              <SummaryPill icon={<CalendarOff size={13} />} count={s.leave} label="Leave" color="text-[#dea439]" bg="bg-[#dea439]/10" />
              <SummaryPill icon={<Gift size={13} />} count={s.holiday} label="Holiday" color="text-purple-600" bg="bg-purple-50" />
            </div>

            {/* Calendar Grid */}
            <div data-testid="calendar-grid" className="bg-white rounded-2xl shadow-sm p-4 animate-slide-up">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAY_HEADERS.map(d => (
                  <div key={d} className="text-center text-[10px] font-bold text-[#1a2529]/30 uppercase">{d}</div>
                ))}
              </div>
              {/* Day cells */}
              <div className="grid grid-cols-7 gap-1">
                {calendarGrid.map((day, idx) => {
                  if (!day) return <div key={`empty-${idx}`} />;
                  const cfg = STATUS_CONFIG[day.status] || STATUS_CONFIG.future;
                  const dayNum = parseInt(day.date.split('-')[2]);
                  const isToday = day.date === new Date().toISOString().split('T')[0];
                  const isSelected = selectedDay?.date === day.date;
                  const isLate = day.is_late && day.status === 'present';

                  return (
                    <button
                      key={day.date}
                      data-testid={`day-${day.date}`}
                      onClick={() => setSelectedDay(isSelected ? null : day)}
                      className={`relative aspect-square rounded-lg flex flex-col items-center justify-center transition-all text-xs font-bold ${cfg.bg} ${cfg.text} ${isSelected ? 'ring-2 ring-[#026536] ring-offset-1 scale-110' : ''} ${isToday ? 'ring-1 ring-[#026536]/50' : ''} ${day.status !== 'future' ? 'cursor-pointer hover:scale-105' : 'cursor-default'}`}
                    >
                      <span className="leading-none">{dayNum}</span>
                      {isLate && <div className="absolute bottom-0.5 h-1 w-1 rounded-full bg-[#dea439]" />}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-[#f4f4f5]">
                {[
                  { color: 'bg-[#026536]', label: 'Present' },
                  { color: 'bg-red-500', label: 'Absent' },
                  { color: 'bg-[#dea439]', label: 'Leave' },
                  { color: 'bg-purple-500', label: 'Holiday' },
                  { color: 'bg-[#1a2529]/10', label: 'Weekend' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className={`h-2.5 w-2.5 rounded-sm ${l.color}`} />
                    <span className="text-[10px] text-[#1a2529]/50 font-medium">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Day Detail */}
            {selectedDay && selectedDay.status !== 'future' && (
              <div data-testid="day-detail" className="bg-white rounded-2xl shadow-sm p-4 animate-slide-up">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-bold text-sm text-[#1a2529]">
                    {formatDateLong(selectedDay.date)}
                  </p>
                  <Badge className={`border-0 text-[10px] ${selectedDay.status === 'present' ? 'bg-[#026536]/10 text-[#026536]' : selectedDay.status === 'absent' ? 'bg-red-50 text-red-500' : selectedDay.status === 'leave' ? 'bg-[#dea439]/10 text-[#dea439]' : selectedDay.status === 'holiday' ? 'bg-purple-50 text-purple-600' : 'bg-[#f4f4f5] text-[#1a2529]/40'}`}>
                    {selectedDay.status.charAt(0).toUpperCase() + selectedDay.status.slice(1)}
                  </Badge>
                </div>
                {selectedDay.status === 'present' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">Check-in</p>
                        <p className="text-sm font-bold text-[#026536]">{formatTime(selectedDay.checkin_time)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">Check-out</p>
                        <p className="text-sm font-bold text-[#1a2529]">{selectedDay.checkout_time ? formatTime(selectedDay.checkout_time) : '-'}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedDay.is_late && <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[10px]">Late Arrival</Badge>}
                      {selectedDay.is_early && <Badge className="bg-orange-50 text-orange-500 border-0 text-[10px]">Early Departure</Badge>}
                      {selectedDay.is_outside_zone && <Badge className="bg-red-50 text-red-500 border-0 text-[10px]">Outside Zone</Badge>}
                      {selectedDay.overtime_minutes > 0 && <Badge className="bg-purple-50 text-purple-600 border-0 text-[10px]">OT {selectedDay.overtime_minutes}m</Badge>}
                    </div>
                  </div>
                )}
                {selectedDay.status === 'holiday' && (
                  <p className="text-sm text-purple-600 font-medium">{selectedDay.holiday_name}</p>
                )}
              </div>
            )}
          </>
        ) : (
          <div data-testid="no-records" className="bg-white rounded-2xl p-8 text-center shadow-sm animate-slide-up">
            <Calendar className="mx-auto text-[#1a2529]/20 mb-3" size={40} />
            <p className="font-semibold text-[#1a2529]">No data available</p>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function SummaryPill({ icon, count, label, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-2 text-center`}>
      <div className={`flex items-center justify-center gap-1 ${color}`}>
        {icon}
        <span className="text-lg font-extrabold">{count}</span>
      </div>
      <p className={`text-[9px] font-semibold uppercase ${color} opacity-60`}>{label}</p>
    </div>
  );
}
